import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './Exhibit.scss';
import './CommonCss.scss';

// Defined outside the component so the editors don't get re-created on every render
const EXHIBIT_QUILL_MODULES = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['clean']
    ],
};

const EXHIBIT_QUILL_FORMATS = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'blockquote', 'code-block',
    'list', 'bullet', 'indent',
    'direction', 'align',
    'link', 'image', 'video'
];

export const createEmptyExhibit = (id) => ({
    id,
    name: '',
    images: [],
    textareas: [{ id: 1, title: 'Text Area 1', content: '' }],
    isValid: false
});

// A single exhibit card. Used inline inside the items table (CreateEstimate)
// so it can be dragged between line items.
export const ExhibitCard = ({ exhibit, letter, setExhibits, canRemove = true }) => {
    const update = (fn) => setExhibits(prev => prev.map(ex => (ex.id === exhibit.id ? fn(ex) : ex)));

    const handleImageAdd = (files) => update(ex => ({
        ...ex,
        images: [...ex.images, ...Array.from(files).map((file, index) => ({
            id: `new_${ex.id}_${Date.now()}_${index}`,
            file: file,
            preview: URL.createObjectURL(file),
            name: file.name,
            type: file.type,
            size: file.size,
            isExisting: false
        }))]
    }));

    const handleImageRemove = (imageId) => update(ex => {
        const imageToRemove = ex.images.find(img => img.id === imageId);
        // Only revoke URL for new images (not existing ones)
        if (imageToRemove && !imageToRemove.isExisting && imageToRemove.preview) {
            URL.revokeObjectURL(imageToRemove.preview);
        }
        return { ...ex, images: ex.images.filter(img => img.id !== imageId) };
    });

    const handleNameChange = (value) => update(ex => ({ ...ex, name: value }));

    const handleTextareaChange = (textareaId, content) => update(ex => ({
        ...ex,
        textareas: ex.textareas.map(textarea => (textarea.id === textareaId ? { ...textarea, content } : textarea))
    }));

    const addTextarea = () => update(ex => {
        // Find the highest numeric ID from existing textareas
        const numericIds = ex.textareas
            .map(t => (typeof t.id === 'string' ? parseInt(t.id, 10) : t.id))
            .filter(id => !isNaN(id) && id > 0);
        const maxId = numericIds.length > 0 ? Math.max(...numericIds) : 0;
        return { ...ex, textareas: [...ex.textareas, { id: maxId + 1, content: '' }] };
    });

    const removeTextarea = (textareaId) => update(ex => ({
        ...ex,
        textareas: ex.textareas.filter(textarea => textarea.id !== textareaId)
    }));

    const removeExhibit = () => setExhibits(prev => {
        // Clean up object URLs to prevent memory leaks
        exhibit.images.forEach(image => {
            if (!image.isExisting && image.preview) {
                URL.revokeObjectURL(image.preview);
            }
        });
        return prev.filter(ex => ex.id !== exhibit.id);
    });

    return (
        <div className="exhibit-item p-3 border bg-light rounded-2">
            <div className="exhibit-item-header d-flex justify-content-between align-items-end mb-3 pb-3 border-bottom">
                <div className="exhibit-header-left d-flex flex-column gap-2 me-2 flex-fill">
                    <h4 className="fw-bold text-black text-opacity-75 mb-0 p14">Exhibit {letter}</h4>
                    <input type="text" value={exhibit.name || ''} onChange={(e) => handleNameChange(e.target.value)} placeholder="Enter exhibit name" className="exhibit-name-input form-control p14" />
                </div>
                <button type="button" className="exhibit-remove-btn btn btn-danger text-sm btn-custom px-3 py-1 m-0" onClick={removeExhibit} disabled={!canRemove}>🗑️ Remove Exhibit</button>
            </div>
            <div className="exhibit-content">
                {/* Image Column */}
                <div className="exhibit-image-column">
                    <label className="exhibit-column-label d-block fw-bold text-black text-opacity-75 mb-2 p14">Images</label>
                    <div className="exhibit-image-upload mb-2">
                        <input type="file" id={`exhibit-images-${exhibit.id}`} multiple accept="image/*" onChange={(e) => handleImageAdd(e.target.files)} className="exhibit-file-input d-none" />
                        <label htmlFor={`exhibit-images-${exhibit.id}`} className="exhibit-file-label btn btn-primary text-sm btn-custom px-2 py-2 m-0 d-inline-flex align-items-center gap-2">
                            <span className="exhibit-icon p16">📷</span>
                            Add Images
                        </label>
                    </div>

                    {exhibit.images.length > 0 && (
                    <div className="exhibit-image-preview">
                        {exhibit.images.map((image) => (
                            <div key={image.id} className="exhibit-image-item position-relative border rounded-2 overflow-hidden bg-white">
                                <img src={image.preview || image.path} alt={image.name} className="exhibit-image w-100 h-100 d-block object-cover" />
                                <button type="button" className="exhibit-image-remove position-absolute text-white border-0 rounded-pill p12 d-flex align-items-center justify-content-center" onClick={() => handleImageRemove(image.id)} title="Remove image" >✕</button>
                                <span className="exhibit-image-name d-block p-2 p12 text-black text-opacity-50 text-center text-break">{image.name}</span>
                            </div>
                        ))}
                    </div>
                    )}
                </div>

                {/* Textarea Column */}
                <div className="exhibit-textarea-column">
                    <label className="exhibit-column-label d-block fw-bold text-black text-opacity-75 mb-2 p14">Text Areas</label>
                    {exhibit.textareas.map((textarea, index) => (
                    <div key={textarea.id} className="exhibit-textarea-item mb-2">
                        <div className="exhibit-textarea-header d-flex justify-content-between align-items-center mb-2">
                            <label className="fw-normal text-black text-opacity-50 p14">Text Area {index + 1}</label>
                            <button type="button" className="exhibit-textarea-remove btn btn-danger text-sm px-2 py-1 m-0 rounded-circle p12" onClick={() => removeTextarea(textarea.id)} disabled={exhibit.textareas.length === 1} title="Remove text area">✕</button>
                        </div>
                        <div className="quill-editor-container ">
                            <ReactQuill className="bg-white" theme="snow" value={textarea.content || ''} onChange={(content) => handleTextareaChange(textarea.id, content)} modules={EXHIBIT_QUILL_MODULES} formats={EXHIBIT_QUILL_FORMATS} placeholder="Enter description..." readOnly={false} />
                        </div>
                    </div>
                    ))}
                    <button type="button" className="exhibit-add-textarea btn btn-primary text-sm btn-custom p-2 m-0 w-100" onClick={addTextarea} >+ Add Text Area</button>
                </div>
            </div>
        </div>
    );
};

// Stand-alone list of exhibits (kept for any other screen that still uses it).
// CreateEstimate now renders ExhibitCard rows inside the items table instead.
const Exhibit = ({ exhibits, setExhibits }) => {
    const addExhibit = () => {
        const maxId = exhibits.length > 0 ? Math.max(...exhibits.map(exhibit => Number(exhibit.id) || 0)) : 0;
        setExhibits(prev => [...prev, createEmptyExhibit(maxId + 1)]);
    };

    return (
        <div className="exhibits-content">
            <div className="exhibit-section m-0">
                {exhibits.map((exhibit, index) => (
                    <div key={exhibit.id} className="mb-3">
                        <ExhibitCard
                            exhibit={exhibit}
                            letter={String.fromCharCode(65 + index)}
                            setExhibits={setExhibits}
                            canRemove={exhibits.length > 1}
                        />
                    </div>
                ))}
                <button type="button" className="exhibit-add-item btn btn-primary text-sm btn-custom px-2 py-3 m-0 w-100" onClick={addExhibit}>+ Add Exhibit</button>
            </div>
        </div>
    );
};

export default Exhibit;
